import { ColorOption, useColor } from 'utils'
import React, { ReactNode } from 'react'
import styled, { css } from 'styled-components'
import {
	CTAStyleProps,
	ctaStyles,
	invertedNormalCtaStyles,
	invertedSolidCtaStyles,
	largeCtaStyles,
	noBorderCtaStyles,
	normalCtaStyles,
	smallCtaStyles,
	solidCtaStyles,
} from './CTA.styles'

export const ctaViewStyles = css<CTAStyleProps>`
	display: block;
	width: 100%;
	background: none;

	&.__pad {
		padding: 0 5px;
	}
	&.__pad-top {
		padding-top: 5vh;
	}
	&.__pad-bot {
		padding-bottom: 1em;
	}
	&.__m-top {
		margin-top: 1em;
	}
	&.__m-bot {
		margin-bottom: 1em;
	}

	&.__qty-1 {
		/* Single button/link */
	}

	&.__qty-2 {
		/* Two button/links */

		&.__layout-horz {
			display: flex;
			flex-direction: row;
			& > * {
				&:nth-child(1) {
					margin-right: 2.5px;
				}
				&:nth-child(2) {
					margin-left: 2.5px;
				}
			}
		}

		&.__layout-vert {
			flex-direction: column;

			& > * {
				margin-left: 0;
				margin-right: 0;
				&:nth-child(1) {
					margin-bottom: 5px;
				}
			}
		}
	}

	& {
		& > a,
		& > button {
			${ctaStyles}
		}
	}

	&.__display-type-normal {
		& > a,
		& > button {
			${normalCtaStyles}
		}
	}

	&.__display-type-solid {
		& > a,
		& > button {
			${solidCtaStyles}
		}
	}

	&.__display-type-no-border {
		& > a,
		& > button {
			${noBorderCtaStyles}
		}
	}

	&.__inverted {
		&.__display-type-normal {
			& > a,
			& > button {
				${invertedNormalCtaStyles}
			}
		}
		&.__display-type-solid {
			& > a,
			& > button {
				${invertedSolidCtaStyles}
			}
		}
		&.__display-type-no-border {
			& > a,
			& > button {
				${invertedNormalCtaStyles}
			}
		}
	}

	&.__size-sm {
		display: inline-block;
		width: auto;

		& > a,
		& > button {
			${smallCtaStyles}
		}
	}

	&.__size-lg {
		& > a,
		& > button {
			${largeCtaStyles}
		}
	}
`

export const CTAView = styled.div`
	${ctaViewStyles}
`

export type CTAProps = {
	// These props apply to the wrapper
	layout?: 'horz' | 'vert'
	pad?: boolean
	padTop?: boolean
	padBot?: boolean
	mTop?: boolean
	mBot?: boolean
	children: ReactNode
	className?: string

	// These props apply to the button/links inside the wrapper
	displayType?: 'normal' | 'solid' | 'no-border'
	invert?: boolean
	size?: 'sm' | 'lg'
	color?: ColorOption
	bgColor?: ColorOption
}

export const CTA = ({
	children,
	className,
	layout = 'horz',
	pad = true,
	padTop,
	padBot,
	mTop,
	mBot,
	displayType = 'normal',
	size = 'lg',
	invert,
	color,
	bgColor,
}: CTAProps) => {
	const colorVal = useColor(
		color || (displayType === 'solid' ? bgColor || 'secondary' : 'secondary')
	)
	const bgColorVal = useColor(bgColor || 'none')

	const childQty = Array.isArray(children) ? children.length : 1

	let ctaClassName = `cta${className ? ` ${className}` : ''}`

	ctaClassName = `${ctaClassName} __layout-${layout}`
	ctaClassName = `${ctaClassName} __display-type-${displayType}`
	ctaClassName = `${ctaClassName} __size-${size}`
	ctaClassName = `${ctaClassName} __qty-${childQty}`

	if (pad) ctaClassName = `${ctaClassName} __pad`
	if (padTop) ctaClassName = `${ctaClassName} __pad-top`
	if (padBot) ctaClassName = `${ctaClassName} __pad-bot`
	if (mTop) ctaClassName = `${ctaClassName} __m-top`
	if (mBot) ctaClassName = `${ctaClassName} __m-bot`

	if (invert) ctaClassName = `${ctaClassName} __inverted`

	return (
		<CTAView className={ctaClassName} colorVal={colorVal} bgVal={bgColorVal}>
			{children}
		</CTAView>
	)
}
